import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { MoreHorizontal } from 'lucide-react'
import type { LocationUserTableRow } from '@/types/location-users'
import { LOCATION_ROLE_LABELS } from '@/types/location-users'
import { formatDistanceToNow } from 'date-fns'

interface LocationUsersTableProps {
  users: LocationUserTableRow[]
  canManage: boolean
  onEditRole: (user: LocationUserTableRow) => void
  onRemove: (user: LocationUserTableRow) => void
  isLoading?: boolean
}

export function LocationUsersTable({
  users,
  canManage,
  onEditRole,
  onRemove,
  isLoading = false,
}: LocationUsersTableProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-24">
        <p className="text-sm text-muted-foreground">Loading users...</p>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="flex justify-center items-center h-24">
        <p className="text-sm text-muted-foreground">No users assigned to this location</p>
      </div>
    )
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'default'
      case 'manager':
        return 'secondary'
      case 'viewer':
        return 'outline'
      default:
        return 'outline'
    }
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Assigned</TableHead>
            {canManage && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">
                {user.user_display_name || 'Unnamed'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">{user.user_email}</TableCell>
              <TableCell>
                <Badge variant={getRoleBadgeVariant(user.location_role)}>
                  {LOCATION_ROLE_LABELS[user.location_role]}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
              </TableCell>
              {canManage && (
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEditRole(user)}>
                        Edit Role
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onRemove(user)}
                        className="text-red-600 focus:text-red-600"
                      >
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
